/**
 * useCSVParser - Parse CSV/Excel files into structured data
 * Supports both .csv and .xlsx formats
 */
import { useState, useCallback } from 'react';
/**
 * Parse CSV text content
 */
function parseCSV(content) {
    const lines = content.trim().split('\n');
    if (lines.length === 0) {
        throw new Error('File is empty');
    }
    // Parse header row
    const headers = parseCSVLine(lines[0]);
    // Parse data rows
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim() === '')
            continue; // Skip empty lines
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => {
            const value = values[index]?.trim() || null;
            row[header] = value;
        });
        rows.push(row);
    }
    return {
        headers,
        rows,
        rowCount: rows.length,
    };
}
/**
 * Parse a single CSV line, handling quoted fields
 */
function parseCSVLine(line) {
    const result = [];
    let current = '';
    let insideQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        if (char === '"') {
            if (insideQuotes && nextChar === '"') {
                // Escaped quote
                current += '"';
                i++; // Skip next quote
            }
            else {
                // Toggle quote state
                insideQuotes = !insideQuotes;
            }
        }
        else if (char === ',' && !insideQuotes) {
            // End of field
            result.push(current.trim());
            current = '';
        }
        else {
            current += char;
        }
    }
    // Add last field
    result.push(current.trim());
    return result;
}
/**
 * Parse XLSX file (requires xlsx library)
 * Falls back to CSV parsing if xlsx not available
 */
async function parseXLSX(file) {
    try {
        // Dynamically import xlsx to keep it optional
        const XLSX = await import('xlsx');
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = e.target?.result;
                    const workbook = XLSX.read(data, { type: 'binary' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    // Convert to CSV-like format
                    const csv = XLSX.utils.sheet_to_csv(worksheet);
                    const parsed = parseCSV(csv);
                    resolve(parsed);
                }
                catch (error) {
                    reject(error);
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsBinaryString(file);
        });
    }
    catch (error) {
        // xlsx not available, try parsing as CSV
        console.warn('xlsx library not available, attempting CSV parse');
        const text = await file.text();
        return parseCSV(text);
    }
}
export function useCSVParser() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const parse = useCallback(async (file) => {
        setIsLoading(true);
        setError(null);
        try {
            // Validate file
            if (!file) {
                throw new Error('No file selected');
            }
            const fileName = file.name.toLowerCase();
            const isCSV = fileName.endsWith('.csv');
            const isXLSX = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
            if (!isCSV && !isXLSX) {
                throw new Error('Only .csv and .xlsx files are supported');
            }
            // Parse based on file type
            let result;
            if (isXLSX) {
                result = await parseXLSX(file);
            }
            else {
                const content = await file.text();
                result = parseCSV(content);
            }
            // Validate result
            if (result.headers.length === 0) {
                throw new Error('No headers found in file');
            }
            if (result.rows.length === 0) {
                throw new Error('No data rows found in file');
            }
            setIsLoading(false);
            return result;
        }
        catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Failed to parse file';
            setError(errorMsg);
            setIsLoading(false);
            return null;
        }
    }, []);
    return { parse, isLoading, error };
}
